import { Button, Empty, Table, Tag } from "@arco-design/web-react";
import type { ColumnProps } from "@arco-design/web-react/es/Table";

import { canUsePermission } from "../../../lib/auth";
import { useCurrentAuthUser } from "../../auth";
import { FINANCE_RECEIVABLE_LIST_EXPORT_PERMISSION } from "../config";
import { useFinanceReceivableOverviewRuntime } from "../runtime/useFinanceOverviewListRuntime";
import { FINANCE_RECEIVABLE_OUTSTANDING_REASONS, formatFinanceReceivableAmount, hasFinanceReceivableAmount } from "../service";
import type { FinanceReceivableOverviewResult, FinanceReceivableOverviewRow } from "../types";
import { FinanceOverviewExportFeedback, FinanceOverviewListFeedback, FinanceOverviewTotals } from "./FinanceOverviewListParts";
import { FinanceReceivableOverviewFilterCard } from "./FinanceReceivableOverviewFilterCard";
import "../../../shared/ui/crmListPageLayout.css";
import "./financeOverview.css";

type Props = {
  /** 点「详情」进这个团的应收清单。 */
  onOpenDetail: (orderId: string) => void;
};

/** 没有值的文字列。 */
function textOrDash(value: string | null) {
  return value ?? <span className="finance-overview-muted">—</span>;
}

/** 合计行尚欠合计后面那句：三类不算尚欠的团各几个，为 0 的也照写。 */
function notOutstandingNote(totals: FinanceReceivableOverviewResult["totals"]) {
  const count = totals.legacySystemCount + totals.rosterUnfinalizedCount + totals.quoteUnreadableCount;
  return `其中 ${count} 个团不算尚欠：旧系统收款 ${totals.legacySystemCount} 个、名单未定稿 ${totals.rosterUnfinalizedCount} 个、报价读不到 ${totals.quoteUnreadableCount} 个`;
}

/** 尚欠那一格：算得出写金额，多收的旁边标「超收 ¥X」；算不出的写原因，不写 0。 */
function Outstanding({ row }: { row: FinanceReceivableOverviewRow }) {
  if (row.outstandingKind !== "computed") {
    return <span className="finance-overview-muted">{FINANCE_RECEIVABLE_OUTSTANDING_REASONS[row.outstandingKind]}</span>;
  }
  return (
    <>
      <span className="finance-overview-money">{formatFinanceReceivableAmount(row.outstandingAmount)}</span>
      {hasFinanceReceivableAmount(row.overpaidAmount) ? (
        <Tag color="orangered" size="small">{`超收 ${formatFinanceReceivableAmount(row.overpaidAmount)}`}</Tag>
      ) : null}
    </>
  );
}

/**
 * 应收列表：全部有团编码的团，一个团一行；当前个人应收、已收、尚欠与这个团的应收清单同一个数。
 * 名单未定稿的团应收那一格写「名单未定稿」、不进合计；尚欠写数字或算不出的原因，尚欠合计只加算得出的；
 * 合计是全部筛选结果；「详情」按接口给的能不能点显示。
 */
export function FinanceReceivableOverviewFeaturePage({ onOpenDetail }: Props) {
  const runtime = useFinanceReceivableOverviewRuntime();
  const data = runtime.data;
  const canExport = canUsePermission(useCurrentAuthUser().data, FINANCE_RECEIVABLE_LIST_EXPORT_PERMISSION);
  const columns: ColumnProps<FinanceReceivableOverviewRow>[] = [
    { title: "团编码", dataIndex: "teamId", width: 180, className: "finance-overview-code-column" },
    { title: "订单号", dataIndex: "orderNo", width: 150, className: "finance-overview-code-column" },
    { title: "线路", render: (_, row) => textOrDash(row.routeName) },
    { title: "出团日期", width: 110, render: (_, row) => textOrDash(row.departureDate) },
    { title: "客户", render: (_, row) => textOrDash(row.customerName) },
    { title: "销售", width: 100, render: (_, row) => textOrDash(row.salesUserName) },
    {
      title: "名单状态",
      width: 100,
      render: (_, row) => (row.rosterFinalized ? <Tag color="green">已定稿</Tag> : <Tag color="orange">未定稿</Tag>),
    },
    { title: "有效人数", dataIndex: "activeMemberCount", width: 90, align: "right" },
    { title: "价格档数", dataIndex: "priceTierCount", width: 90, align: "right" },
    {
      title: "当前个人应收",
      width: 150,
      align: "right",
      render: (_, row) =>
        row.rosterFinalized ? (
          <span className="finance-overview-money">{formatFinanceReceivableAmount(row.currentReceivableTotal)}</span>
        ) : (
          <span className="finance-overview-muted">名单未定稿</span>
        ),
    },
    {
      title: "已收",
      width: 130,
      align: "right",
      render: (_, row) => <span className="finance-overview-money">{formatFinanceReceivableAmount(row.receivedTotal)}</span>,
    },
    { title: "尚欠", width: 170, align: "right", render: (_, row) => <Outstanding row={row} /> },
    ...(data?.canViewDetail
      ? [
          {
            title: "操作",
            width: 80,
            render: (_: unknown, row: FinanceReceivableOverviewRow) => (
              <Button type="text" onClick={() => onOpenDetail(row.orderId)}>详情</Button>
            ),
          },
        ]
      : []),
  ];
  return (
    <main className="finance-overview-page crm-list-page" aria-label="应收列表">
      <FinanceReceivableOverviewFilterCard
        filters={runtime.draft}
        onChange={runtime.setDraft}
        onSearch={runtime.search}
        onReset={runtime.reset}
      />
      <section className="crm-list-panel" aria-busy={runtime.loading}>
        <div className="crm-list-section-header">
          <div className="crm-list-title-row">
            <h2>应收列表</h2>
            <div className="crm-list-statline">共 {data?.total ?? 0} 个团</div>
          </div>
          {canExport ? (
            <div className="crm-list-table-actions">
              <Button loading={runtime.exporting} onClick={() => void runtime.exportExcel()}>
                导出 Excel
              </Button>
            </div>
          ) : null}
        </div>
        <p className="finance-overview-note">
          已收只算通过本系统团专属二维码、银行卡收到的钱；团款目前大多还在旧系统收，这里的已收会偏少、尚欠会偏多。旧系统同步来的团不算尚欠。银行到账、待退款、收款状态、团专属收款通道暂不显示。
        </p>
        <FinanceOverviewExportFeedback listName="应收列表" error={runtime.exportError} />
        <FinanceOverviewListFeedback
          listName="应收列表"
          limitExceeded={runtime.limitExceeded}
          failed={runtime.failed}
          onRetry={runtime.refresh}
        />
        {data ? (
          <FinanceOverviewTotals
            label="应收列表合计"
            items={[
              { name: "团数", value: String(data.totals.teamCount) },
              { name: "名单未定稿", value: String(data.totals.rosterUnfinalizedCount), note: "个团，不进应收合计" },
              {
                name: "当前个人应收合计",
                value: formatFinanceReceivableAmount(data.totals.currentReceivableTotal),
              },
              { name: "已收合计", value: formatFinanceReceivableAmount(data.totals.receivedTotal) },
              {
                name: "尚欠合计",
                value: formatFinanceReceivableAmount(data.totals.outstandingTotal),
                note: notOutstandingNote(data.totals),
              },
            ]}
          />
        ) : null}
        <Table<FinanceReceivableOverviewRow>
          rowKey="orderId"
          loading={runtime.loading}
          columns={columns}
          data={data?.items ?? []}
          scroll={{ x: 1640 }}
          noDataElement={<Empty description={runtime.limitExceeded ? "请加出团日期范围后再查" : "没有符合条件的团"} />}
          pagination={{
            current: runtime.page,
            pageSize: runtime.pageSize,
            total: data?.total ?? 0,
            onChange: runtime.setPage,
          }}
        />
      </section>
    </main>
  );
}
